import { Helmet } from "react-helmet-async";
import { FIRM } from "@/constants/firmInfo";

// Central SEO component: sets Title, Meta description, OpenGraph, Twitter Cards,
// canonical URL, and injects one or more JSON-LD structured data blocks.
export const SEO = ({
  title,
  description,
  path = "/",
  image,
  jsonLd,
  type = "website",
}) => {
  const fullTitle = title ? `${title} | ${FIRM.shortName}` : FIRM.name;
  const canonicalUrl = `${FIRM.siteUrl}${path}`;
  const ogImage = image || `${FIRM.siteUrl}/images/logo-full.png`;
  let jsonLdBlocks = [];
  if (Array.isArray(jsonLd)) {
    jsonLdBlocks = jsonLd;
  } else if (jsonLd) {
    jsonLdBlocks = [jsonLd];
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* OpenGraph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={FIRM.name} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLdBlocks.map((block, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

// Reusable JSON-LD builders
export const buildLegalServiceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LegalService",
  name: FIRM.name,
  image: `${FIRM.siteUrl}${FIRM.logoFull}`,
  telephone: FIRM.phoneTel,
  email: FIRM.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: FIRM.addressLine1,
    addressLocality: FIRM.city,
    addressRegion: FIRM.state,
    postalCode: FIRM.zip,
    addressCountry: "US",
  },
  areaServed: "Dallas-Fort Worth, TX",
  priceRange: "$$",
  url: FIRM.siteUrl,
  founder: {
    "@type": "Person",
    name: FIRM.founder,
  },
  foundingDate: String(FIRM.foundedYear),
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday"],
      opens: "10:00",
      closes: "17:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Friday"],
      opens: "10:00",
      closes: "13:00",
    },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Legal Services",
    itemListElement: [
      "Family Immigration",
      "Employment Immigration",
      "Business Immigration",
      "US Citizenship",
      "Business Law",
      "Wills & Trusts",
      "Family Law",
    ].map((name) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name } })),
  },
});

export const buildAttorneySchema = () => ({
  "@context": "https://schema.org",
  "@type": "Attorney",
  name: FIRM.founder,
  jobTitle: FIRM.founderTitle,
  worksFor: {
    "@type": "LegalService",
    name: FIRM.name,
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: FIRM.addressLine1,
    addressLocality: FIRM.city,
    addressRegion: FIRM.state,
    postalCode: FIRM.zip,
    addressCountry: "US",
  },
  telephone: FIRM.phoneTel,
  email: FIRM.email,
});

export const buildFAQSchema = (faqs = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.answer,
    },
  })),
});

// Builds a LegalService schema block enriched with AggregateRating + individual
// Review entries so Google can render star-rating rich snippets in search results.
export const buildReviewsSchema = (testimonials = []) => {
  if (!testimonials.length) return null;
  const avg =
    testimonials.reduce((sum, t) => sum + (t.rating || 5), 0) / testimonials.length;
  return {
    "@context": "https://schema.org",
    "@type": "LegalService",
    name: FIRM.name,
    url: FIRM.siteUrl,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avg.toFixed(1),
      reviewCount: testimonials.length,
      bestRating: "5",
    },
    review: testimonials.map((t) => ({
      "@type": "Review",
      author: { "@type": "Person", name: t.name },
      reviewRating: {
        "@type": "Rating",
        ratingValue: String(t.rating || 5),
        bestRating: "5",
      },
      reviewBody: t.quote,
    })),
  };
};

export const buildBreadcrumbSchema = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: item.name,
    item: `${FIRM.siteUrl}${item.path}`,
  })),
});
